import type { DevicePlatform } from "@led-signage/shared/device-api";
import type { CreateDeviceResponse } from "@led-signage/shared/management-api";
import { useState } from "react";
import { newIdempotencyKey } from "./api/client.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import { api, usingFixtures } from "./api/index.js";
import { PermissionNotice, useCan } from "./session.js";

/**
 * 端末の登録とenrollment tokenの発行。
 *
 * 平文のtokenは応答1回だけ返り、クラウドはprefixとhashしか保存しない。
 * したがってこの画面は**表示した1回が唯一の機会**であり、保存も再表示もできない。
 */

type State =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "issued"; response: CreateDeviceResponse }
  | { kind: "failed"; failure: ActionFailure };

function expiresLabel(timestamp: string | null): string {
  if (timestamp === null) return "—";
  const parsed = new Date(timestamp);
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
}

export function DeviceEnrollment() {
  const [displayId, setDisplayId] = useState("");
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState<DevicePlatform>("windows");
  const [state, setState] = useState<State>({ kind: "idle" });
  const [copied, setCopied] = useState(false);
  /*
   * 同じ鍵で再送すると、端末がまだ未登録なら新しいtokenが発行される（PR #62）。
   * 応答を取り逃したときに端末を二重に作らず、やり直せるようにするため、
   * 入力内容を変えるまで鍵を保持する。
   */
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  const allowed = useCan("device:manage");
  const ready =
    allowed && displayId.trim().length > 0 && name.trim().length > 0;

  async function register() {
    setState({ kind: "running" });
    setCopied(false);
    try {
      const response = await api.createDevice(
        displayId.trim(),
        { name: name.trim(), platform },
        idempotencyKey,
      );
      setState({ kind: "issued", response });
    } catch (error) {
      setState({ kind: "failed", failure: describeFailure(error) });
    }
  }

  function reset() {
    setState({ kind: "idle" });
    setCopied(false);
    setIdempotencyKey(newIdempotencyKey());
    setName("");
  }

  return (
    <section className="panel enrollment-panel">
      <h3>端末を登録してenrollment tokenを発行</h3>
      <p className="resource-note">
        現地の再生端末をLEDへ紐づけ、初回登録用のtokenを発行します。
      </p>

      <PermissionNotice permission="device:manage" action="端末の登録" />

      {usingFixtures && (
        <div className="resource-note resource-note--fixture">
          ローカル確認用の表示です。発行は実APIでのみ実行できます。
        </div>
      )}

      <label className="field">
        <span>対象のLED（Display ID）</span>
        <input
          type="text"
          value={displayId}
          placeholder="dsp_..."
          disabled={state.kind === "issued"}
          onChange={(event) => {
            setDisplayId(event.target.value);
            setIdempotencyKey(newIdempotencyKey());
          }}
        />
      </label>

      <label className="field">
        <span>端末の名前</span>
        <input
          type="text"
          value={name}
          placeholder="例: 那覇メイン N100-WIN-01"
          disabled={state.kind === "issued"}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </label>

      <label className="field">
        <span>プラットフォーム</span>
        <select
          value={platform}
          disabled={state.kind === "issued"}
          onChange={(event) => {
            setPlatform(event.target.value as DevicePlatform);
          }}
        >
          <option value="windows">Windows</option>
          <option value="linux">Linux</option>
        </select>
      </label>

      {state.kind === "failed" && (
        <div className="inline-alert inline-alert--error">
          <b>発行できませんでした</b>
          <span>{state.failure.message}</span>
          <AuthenticationRecoveryButton failure={state.failure} />
          {state.failure.code === "DEVICE_ALREADY_REGISTERED" && (
            <span>
              このLEDには<strong>登録済みの</strong>端末が既にあります。入れ替えるには、
              先に既存の端末を退役させてください。まだenrollしていない端末であれば、
              同じ内容で発行し直せます。
            </span>
          )}
          {state.failure.code === "NOT_FOUND" && (
            <span>Display IDが存在しないか、別の組織のものです。</span>
          )}
        </div>
      )}

      {state.kind === "issued" && (
        <TokenResult
          response={state.response}
          copied={copied}
          onCopied={() => {
            setCopied(true);
          }}
          onReset={reset}
        />
      )}

      {state.kind !== "issued" && (
        <div className="editor-actions">
          <button
            type="button"
            disabled={!ready || state.kind === "running"}
            onClick={() => void register()}
          >
            {state.kind === "running" ? "発行中…" : "登録してtokenを発行"}
          </button>
        </div>
      )}

      {state.kind !== "issued" && !ready && (
        <p className="resource-note">
          発行できません: Display IDと端末の名前を入力してください。
        </p>
      )}
    </section>
  );
}

function TokenResult({
  response,
  copied,
  onCopied,
  onReset,
}: {
  response: CreateDeviceResponse;
  copied: boolean;
  onCopied: () => void;
  onReset: () => void;
}) {
  const token = response.enrollmentToken;

  // 登録済み・退役済みの端末には使えるtokenが存在しない（PR #62でnullにした）。
  if (token === null) {
    return (
      <div className="inline-alert">
        <b>この端末は既に登録済みです</b>
        <span>
          使用できるenrollment tokenはありません。端末ID {response.device.id}
        </span>
        <button type="button" onClick={onReset}>
          別の端末を登録する
        </button>
      </div>
    );
  }

  return (
    <div className="token-result">
      <div className="inline-alert inline-alert--error">
        <b>この画面を閉じると二度と表示できません</b>
        <span>
          クラウドは識別用のprefixとhashだけを保存し、平文は保持しません。
          いま控えてください。取り逃した場合は、同じ内容でもう一度発行し直します。
        </span>
      </div>

      <label className="field">
        <span>enrollment token</span>
        <textarea readOnly rows={3} value={token} className="token-field" />
      </label>

      <div className="editor-actions">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(token).then(onCopied);
          }}
        >
          {copied ? "コピーしました" : "tokenをコピー"}
        </button>
        <button type="button" onClick={onReset}>
          控えました。閉じる
        </button>
      </div>

      <dl className="token-meta">
        <div>
          <dt>端末ID</dt>
          <dd>{response.device.id}</dd>
        </div>
        <div>
          <dt>対象のLED</dt>
          <dd>{response.device.displayId}</dd>
        </div>
        <div>
          <dt>有効期限</dt>
          <dd>{expiresLabel(response.enrollmentTokenExpiresAt)}</dd>
        </div>
      </dl>

      <p className="resource-note">
        現地の端末で <code>enroll</code> を実行してください。enrollは一度しか成功せず、
        成功した時点で残りのtokenは使えなくなります。
      </p>
    </div>
  );
}
